const DEFAULT_LINE_RATING_ID = "1";

const isTruthyFlag = (v) => v === true || v === 1 || v === "1";

export function findDefaultRating(ratingData = [], isGlobalRatingEnabled = false) {
  if (!isTruthyFlag(isGlobalRatingEnabled)) return null;
  const ratings = Array.isArray(ratingData) ? ratingData : [];
  return ratings.find((r) => isTruthyFlag(r?.is_default)) || null;
}

const isEmptyId = (id) => id === null || id === undefined || id === "" || id === "null";

// A line/comment that carries only `defaultRating_id` has no explicit selection of its own — the
// id just mirrors whatever was last applied to it. So an id pointing at the template default, or
// at Inspected (id 1) which ships on new lines, counts as "nothing selected" rather than a pick.
// The template default is re-applied for display by `applyDefaultRating` when global ratings are
// on, which keeps it following the template instead of being frozen onto the line/comment.
const isImplicitDefaultId = (id, ratingData) => {
  if (String(id) === DEFAULT_LINE_RATING_ID) return true;
  const templateDefault = ratingData.find((r) => isTruthyFlag(r?.is_default));
  return !!templateDefault && String(templateDefault.Id) === String(id);
};

// Rating rows reach the editor in two casings: the template's own `rating_data` is PascalCase,
// while the company pool (GET /ratings) and comment-library rows are lowercase. Read both.
const ratingId = (rating) => rating?.Id ?? rating?.id;
const ratingName = (rating) => rating?.Name ?? rating?.name;
const ratingColor = (rating) => rating?.Color ?? rating?.color;
const summaryFlag = (rating) => Number(rating?.include_in_summary ?? (rating?.inSummary ? 1 : 0)) || 0;

const isPresent = (value) => value !== null && value !== undefined;

// A comment-library join row ties a rating to a comment, so it names the comment (`comment_id`),
// carries a `pivot`, or nests the rating under `rating`. A bare rating does none of these.
const isJoinRow = (entry) => isPresent(entry?.comment_id) || isPresent(entry?.pivot) || isPresent(entry?.rating);

// The company rating a comment-library rating entry points at. Where the id sits depends on how the
// library row was loaded: on the join row's pivot (`rating.pivot.rating_id` / `pivot.rating_id`), as
// a flat `rating_id`, as the flattened `Id`, or on the nested `rating.id`. Plain `id` is only the
// rating's id when the entry is the bare rating itself; on a join row it is the join row's own id,
// so it is not read there — a join row with no explicit rating id has none. Without an id the rating
// can be neither registered on the template nor given its definition from the company pool.
export function libraryRatingId(entry) {
  const explicitId =
    entry?.rating?.pivot?.rating_id ||
    entry?.pivot?.rating_id ||
    entry?.rating_id ||
    entry?.Id ||
    entry?.rating?.id;
  if (explicitId) return explicitId;
  return isJoinRow(entry) ? undefined : entry?.id;
}

// The rating shape POST template/ratings expects.
export function toRatingPayload(rating) {
  return {
    Id: parseInt(ratingId(rating)),
    Name: ratingName(rating),
    Color: ratingColor(rating),
    Definition: rating.Definition ?? rating.definition,
    include_in_summary: summaryFlag(rating),
    is_default: Number(rating.is_default) || 0,
    order: rating.order ?? null,
  };
}

// A rating with no `order` sorts ahead of the numbered ones, so newly added ratings reappear at
// the top after a refresh. Renumber by position (the 1-based scheme the reorder call persists).
export function withSequentialOrder(ratings) {
  return ratings.map((rating, index) => ({ ...rating, order: index + 1 }));
}

// A content fingerprint for a rating list, for telling "the same ratings in a new array" from a
// real change without a deep compare.
export function ratingListSignature(ratingData) {
  return (Array.isArray(ratingData) ? ratingData : [])
    .map((rating) =>
      [
        ratingId(rating),
        ratingName(rating),
        ratingColor(rating),
        summaryFlag(rating),
        Number(rating?.is_default) || 0,
        rating?.order ?? "",
      ].join(":")
    )
    .join("|");
}

// Two rows describing the same rating. Ids are compared loosely because the API mixes strings and
// numbers, then name + color + summary flag as a fallback — a company rating can carry a
// different id per template, which is why the Texas comment-library path matches this way too.
export function isSameRating(a, b) {
  if (!a || !b) return false;
  const idA = ratingId(a);
  const idB = ratingId(b);
  if (!isEmptyId(idA) && !isEmptyId(idB) && String(idA) === String(idB)) return true;
  return (
    ratingName(a) === ratingName(b) &&
    ratingColor(a) === ratingColor(b) &&
    summaryFlag(a) === summaryFlag(b)
  );
}

// The ratings out of `ratings` that `ratingData` doesn't already have, deduped against each other
// so two comments carrying the same new rating only report it once.
export function collectMissingRatings(ratings, ratingData = []) {
  const templateRatings = Array.isArray(ratingData) ? ratingData : [];
  const missing = [];
  (Array.isArray(ratings) ? ratings : []).forEach((rating) => {
    if (!rating) return;
    if (templateRatings.some((existing) => isSameRating(existing, rating))) return;
    if (missing.some((added) => isSameRating(added, rating))) return;
    missing.push(rating);
  });
  return missing;
}

// The template's ratings followed by the company-pool ratings it doesn't have yet, in the
// template's own PascalCase shape. A rating created in the Comment Library only exists in the pool
// until a comment brings it onto the template, so a picker built from `rating_data` alone never
// offers it.
export function withPoolRatings(templateRatings = [], poolRatings = []) {
  const poolAsTemplateRatings = (Array.isArray(poolRatings) ? poolRatings : [])
    .filter((rating) => rating && !isTruthyFlag(rating.is_deleted))
    .map((rating) => ({
      Id: ratingId(rating),
      Name: ratingName(rating),
      Color: ratingColor(rating),
      Definition: rating.Definition ?? rating.definition ?? "",
      include_in_summary: summaryFlag(rating),
      is_default: 0,
    }));
  const onTemplate = Array.isArray(templateRatings) ? templateRatings : [];
  return [...onTemplate, ...collectMissingRatings(poolAsTemplateRatings, onTemplate)];
}

const hasRatingOrder = (rating) => {
  const order = rating?.order;
  if (order === null || order === undefined || order === "") return false;
  return Number.isFinite(Number(order));
};

export function sortRatingsByOrder(ratingData) {
  const ratings = Array.isArray(ratingData) ? ratingData : [];
  return [...ratings].sort((a, b) => {
    const hasOrderA = hasRatingOrder(a);
    const hasOrderB = hasRatingOrder(b);
    if (hasOrderA && hasOrderB) return Number(a.order) - Number(b.order);
    if (hasOrderA !== hasOrderB) return hasOrderA ? -1 : 1;
    return 0;
  });
}

export function isRemovedDefaultRating(owner, ratingId) {
  const removedId = owner?.removed_default_rating_id;
  if (isEmptyId(removedId) || isEmptyId(ratingId)) return false;
  return String(removedId) === String(ratingId);
}

const dropDeletedRatings = (base, ratingData) => {
  if (ratingData.length === 0) return base.filter(Boolean);
  return base.filter((r) => r && ratingData.some((option) => String(option.Id) === String(r.Id)));
};

const applyDefaultRating = (owner, base, ratingData, isGlobalRatingEnabled) => {
  const live = dropDeletedRatings(base, ratingData);
  const withoutRemoved = live.filter((r) => !isRemovedDefaultRating(owner, r.Id));
  // No template default to apply (global ratings off, or none marked default) — the line/comment
  // keeps whatever it has selected.
  const defaultRating = findDefaultRating(ratingData, isGlobalRatingEnabled);
  if (!defaultRating || isRemovedDefaultRating(owner, defaultRating.Id)) return withoutRemoved;
  const alreadyIncluded = withoutRemoved.some((r) => String(r?.Id) === String(defaultRating.Id));
  return alreadyIncluded ? withoutRemoved : [...withoutRemoved, defaultRating];
};

export function stripDefaultRating(values, ratingData = [], isGlobalRatingEnabled = false) {
  const list = Array.isArray(values) ? values : [];
  const defaultRating = findDefaultRating(ratingData, isGlobalRatingEnabled);
  if (!defaultRating) return list;
  return list.filter((r) => String(r?.Id) !== String(defaultRating.Id));
}

const stripDefaultRatingFromComment = (comment, ratingData, isGlobalRatingEnabled) => {
  const controls = comment?.comment_input_controls;
  if (!Array.isArray(controls)) return comment;
  let changed = false;
  const nextControls = controls.map((control) => {
    if (control?.type !== "Rating" || !Array.isArray(control.data)) return control;
    const data = stripDefaultRating(control.data, ratingData, isGlobalRatingEnabled);
    if (data.length === control.data.length) return control;
    changed = true;
    return { ...control, data };
  });
  if (!changed) return comment;
  const next = { ...comment, comment_input_controls: nextControls };
  if (Array.isArray(next.rating)) next.rating = stripDefaultRating(next.rating, ratingData, isGlobalRatingEnabled);
  return next;
};

const stripDefaultRatingFromLine = (line, ratingData, isGlobalRatingEnabled) => {
  const ratings = Array.isArray(line?.line_rating_data)
    ? stripDefaultRating(line.line_rating_data, ratingData, isGlobalRatingEnabled)
    : line?.line_rating_data;
  const comments = Array.isArray(line?.comments)
    ? line.comments.map((comment) => stripDefaultRatingFromComment(comment, ratingData, isGlobalRatingEnabled))
    : line?.comments;

  const lineChanged = Array.isArray(ratings) && ratings.length !== line.line_rating_data.length;
  const commentsChanged =
    Array.isArray(comments) && comments.some((comment, idx) => comment !== line.comments[idx]);
  if (!lineChanged && !commentsChanged) return line;
  const next = { ...line, comments };
  if (lineChanged) {
    next.line_rating_data = ratings;
    next.defaultRating_id = ratings.length > 0 ? Number(ratings[0].Id) : null;
  }
  return next;
};

export function stripDefaultRatingFromCategories(categories, ratingData = [], isGlobalRatingEnabled = false) {
  if (!Array.isArray(categories)) return categories;
  if (!findDefaultRating(ratingData, isGlobalRatingEnabled)) return categories;
  return categories.map((category) => {
    if (!Array.isArray(category?.lines)) return category;
    const lines = category.lines.map((line) => stripDefaultRatingFromLine(line, ratingData, isGlobalRatingEnabled));
    return lines.some((line, idx) => line !== category.lines[idx]) ? { ...category, lines } : category;
  });
}

export function resolveRemovedDefaultRatingId(values, ratingData = [], isGlobalRatingEnabled = false) {
  const defaultRating = findDefaultRating(ratingData, isGlobalRatingEnabled);
  if (!defaultRating) return null;
  const stillSelected = (Array.isArray(values) ? values : []).some(
    (r) => String(r?.Id) === String(defaultRating.Id)
  );
  if (stillSelected) return null;
  const numericId = Number(defaultRating.Id);
  return Number.isNaN(numericId) ? defaultRating.Id : numericId;
}

export function resolveLineRatingsFromLine(line, ratingData = [], isGlobalRatingEnabled = false) {
  const ratings = Array.isArray(ratingData) ? ratingData : [];
  const existing = line?.line_rating_data;
  let base;
  if (Array.isArray(existing) && existing.length > 0) {
    base = existing;
  } else if (isEmptyId(line?.defaultRating_id) || isImplicitDefaultId(line.defaultRating_id, ratings)) {
    base = [];
  } else {
    const single = ratings.find((r) => String(r.Id) === String(line.defaultRating_id));
    base = single ? [single] : [];
  }
  return applyDefaultRating(line, base, ratings, isGlobalRatingEnabled);
}

export function resolveCommentRatingsFromComment(comment, ratingData = [], isGlobalRatingEnabled = false) {
  const ratings = Array.isArray(ratingData) ? ratingData : [];
  const ratingControl = Array.isArray(comment?.comment_input_controls)
    ? comment.comment_input_controls.find((item) => item.type === "Rating")
    : null;
  const existing = ratingControl?.data;

  let base;
  if (Array.isArray(existing) && existing.length > 0) {
    base = existing.map((item) => resolveLiveRating(item, ratings));
  } else if (isEmptyId(comment?.defaultRating_id) || isImplicitDefaultId(comment.defaultRating_id, ratings)) {
    base = [];
  } else {
    const single = ratings.find((r) => String(r.Id) === String(comment.defaultRating_id));
    base = single ? [single] : [];
  }

  return applyDefaultRating(comment, base, ratings, isGlobalRatingEnabled);
}

export function resolveLiveRating(ratingRef, ratingData = []) {
  if (!ratingRef) return null;
  const ratings = Array.isArray(ratingData) ? ratingData : [];
  // Read both casings: a comment-library rating carries a lowercase `id`, and comparing only `.Id`
  // matched "undefined" === "undefined" — resolving every such rating to the first option.
  const refId = ratingId(ratingRef);
  if (isEmptyId(refId)) return ratingRef;
  const match = ratings.find((r) => String(ratingId(r)) === String(refId));
  return match || ratingRef;
}

export function normalizeLineLevelRating(line, ratingData = []) {
  if (!line) return line;
  if (Array.isArray(line.line_rating_data) && line.line_rating_data.length > 0) {
    return line;
  }

  const ratingId = line.defaultRating_id;
  if (isEmptyId(ratingId) || isRemovedDefaultRating(line, ratingId)) {
    return { ...line, defaultRating_id: null, line_rating_data: [] };
  }

  const ratings = Array.isArray(ratingData) ? ratingData : [];

  // New templates/lines often ship with Inspected (id 1) — and the template default can be
  // mirrored onto the line the same way — with no explicit line_rating_data. Neither is a pick of
  // the line's own, so it must not be migrated into line_rating_data, which would freeze it on as
  // an explicit selection. See `isImplicitDefaultId`.
  if (isImplicitDefaultId(ratingId, ratings)) {
    return { ...line, defaultRating_id: null, line_rating_data: [] };
  }

  // Legacy lines may only have defaultRating_id — migrate into line_rating_data.
  const rating = ratings.find((r) => String(r.Id) === String(ratingId));
  if (!rating) {
    return { ...line, defaultRating_id: null, line_rating_data: [] };
  }

  return {
    ...line,
    defaultRating_id: Number(ratingId),
    line_rating_data: [rating],
  };
}

export function normalizeLineLevelRatings(categories, ratingData = []) {
  if (!Array.isArray(categories)) return [];
  const ratings = Array.isArray(ratingData) ? ratingData : [];
  return categories.map((category) => ({
    ...category,
    lines: Array.isArray(category?.lines)
      ? category.lines.map((line) => normalizeLineLevelRating(line, ratings))
      : [],
  }));
}